/* Yo Gorilla Mats, direction C "Measured": small progressive enhancements, no dependencies.
   1. Dimension callouts on photos draw in once they are in view (static if this file never runs: the
      draw-in styles only apply under html.ygm-js, and reduced motion skips the transition).
   2. Framed videos: muted, looping, preload="none". Start only while in view, pause out of view; under
      prefers-reduced-motion they stay on the poster with a play button. A pause/play button is always offered.
   3. Photo strips get previous/next buttons. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hasIO = 'IntersectionObserver' in window;

  function initDims(root) {
    const els = root.querySelectorAll('[data-dims]:not(.is-in)');
    if (!hasIO) { els.forEach((d) => d.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { threshold: 0.45 });
    els.forEach((d) => io.observe(d));
  }

  function play(v) {
    if (v.preload === 'none') v.preload = 'auto';
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }

  function initVideos(root) {
    const vids = root.querySelectorAll('video[data-ygm-video]:not([data-ready])');
    if (!vids.length) return;
    const io = hasIO ? new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const v = e.target;
        v._inView = e.isIntersecting;
        if (e.isIntersecting) { if (!v._userPaused && !reduce.matches) play(v); }
        else if (!v.paused) v.pause();
      });
    }, { threshold: 0.4 }) : null;
    vids.forEach((v) => {
      v.dataset.ready = '1';
      v.muted = true;
      const btn = v.parentElement.querySelector('[data-ygm-video-toggle]');
      if (btn) {
        const sync = () => {
          btn.classList.toggle('is-paused', v.paused);
          btn.setAttribute('aria-label', v.paused ? 'Play video' : 'Pause video');
        };
        btn.hidden = false;
        sync();
        v.addEventListener('play', sync);
        v.addEventListener('pause', sync);
        btn.addEventListener('click', () => {
          if (v.paused) { v._userPaused = false; play(v); } else { v._userPaused = true; v.pause(); }
        });
      }
      if (io) io.observe(v);
    });
    reduce.addEventListener?.('change', () => {
      vids.forEach((v) => { if (reduce.matches) v.pause(); else if (v._inView && !v._userPaused) play(v); });
    });
  }

  // 3. Photo strips: previous/next buttons scroll by most of a screen; hidden when everything fits.
  function initStrips(root) {
    root.querySelectorAll('[data-strip-nav]').forEach((nav) => {
      const sec = nav.closest('section');
      const track = sec && sec.querySelector('[data-strip-track]');
      if (!track || nav.dataset.ready) return;
      nav.dataset.ready = '1';
      const [prev, next] = nav.querySelectorAll('[data-strip]');
      const sync = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        nav.hidden = max <= 0;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
      };
      nav.addEventListener('click', (e) => {
        const b = e.target.closest('[data-strip]');
        if (!b) return;
        track.scrollBy({ left: Number(b.dataset.strip) * track.clientWidth * 0.8, behavior: reduce.matches ? 'auto' : 'smooth' });
      });
      track.addEventListener('scroll', () => { cancelAnimationFrame(track._raf); track._raf = requestAnimationFrame(sync); }, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  const init = (root = document) => { initDims(root); initVideos(root); initStrips(root); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
